import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Settings, Brain } from "lucide-react";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import NotificationPreferences from "@/components/NotificationPreferences";
import ChangePassword from "@/components/ChangePassword";
import OfflinePackCard from "@/components/OfflinePackCard";
import SeasonSettings from "@/components/SeasonSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [selectedModel, setSelectedModel] = useState('gpt-5.2');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const aiModels = [
    { value: 'gpt-5.2', label: 'GPT-5.2 (OpenAI)' },
    { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
    { value: 'gemini-3-flash', label: 'Gemini 3 Flash' }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/user/profile`, { headers });
      setProfile(response.data);
      setSelectedModel(response.data.selected_ai_model);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/user/preferences`, {
        selected_ai_model: selectedModel
      }, { headers });
      
      toast.success('Preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 lg:p-12">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tighter-xl">
            PROFILE
          </h1>
          <p className="text-muted-foreground mt-2">Manage your account and preferences</p>
        </div>

        {/* Profile Info */}
        <Card className="bg-card border-border" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Name</label>
                <p className="text-lg font-medium text-white" data-testid="profile-name">{profile?.name}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Email</label>
                <p className="text-lg font-medium text-white" data-testid="profile-email">{profile?.email}</p>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Member Since</label>
              <p className="text-lg font-medium text-white" data-testid="profile-created">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '-'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card className="bg-card border-border" data-testid="preferences-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <label className="text-sm font-medium text-white flex items-center gap-2">
                <Brain className="w-4 h-4 text-primary" />
                Preferred AI Coach Model
              </label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger data-testid="model-preference-selector" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {aiModels.map(model => (
                    <SelectItem key={model.value} value={model.value}>
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose your preferred AI model for coaching feedback. This will be used by default in the AI Coach.
              </p>
            </div>

            <Button
              data-testid="save-preferences-button"
              onClick={handleSavePreferences}
              disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white font-bold uppercase"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <PushNotificationToggle />

        {/* Notification Preferences (push types, timing, timezone, in-app toggles) */}
        <NotificationPreferences />

        {/* Change Password */}
        <ChangePassword />

        {/* Seasonal Periodization — hemisphere + season override */}
        <Card className="bg-card border-border" data-testid="season-settings-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Seasonal Training
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SeasonSettings />
          </CardContent>
        </Card>

        {/* Offline Pack */}
        <OfflinePackCard />

        {/* Subscription Info */}
        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-heading font-black text-white mb-2">PIVOT PERFORMANCE</h3>
                <p className="text-muted-foreground mb-1">Part of the Pivot Ecosystem</p>
                <p className="text-sm text-white font-medium">$9.99/month • Generational summer</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground uppercase mb-1">Status</p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-sm font-bold text-green-500">ACTIVE</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;